/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * C2PA fragmented-BMFF Merkle-tree verification, ported from c2pa-rs
 * (`MerkleMap::check_merkle_tree`, `C2PAMerkleTree::to_layout`, Apache-2.0).
 *
 * A `c2pa.hash.bmff.v2/v3` assertion carries one `MerkleMap` per rendition
 * (matched by `uniqueId`/`localId`) with the init-segment hash and the stored
 * Merkle row (`hashes`, typically the single root). Each media fragment carries a
 * `uuid`/"merkle" box with its leaf `location` and the proof hashes needed to
 * climb to that row. The leaf is the fragment's BMFF content hash.
 */

import { decodeFirst } from '../cbor/decode'
import { readC2paBox, findAllC2paBoxes, parseC2paBox } from './c2paBox'
import { computeBmffHash, webcryptoDigestForC2pa, type BmffExclusion } from './bmffHash'

/** Thrown when a hard-binding hash alg is absent or not on the §13.1 list (→ `algorithm.unsupported`). */
export class UnsupportedAlgError extends Error {}

/**
 * Resolves the effective hash algorithm for a Merkle map (§15.4: map alg → assertion
 * alg → claim alg, nearest wins) to a WebCrypto digest name, throwing
 * {@link UnsupportedAlgError} when none is present or the value is unsupported.
 */
function resolveDigest (mapAlg: string | undefined, assertionAlg: string | undefined, claimAlg: string | undefined): string {
    const digest = webcryptoDigestForC2pa(mapAlg ?? assertionAlg ?? claimAlg)
    if (!digest) throw new UnsupportedAlgError(`unsupported hash algorithm: ${mapAlg ?? assertionAlg ?? claimAlg ?? '(none)'}`)
    return digest
}

/** A C2PA `MerkleMap` from a hash assertion's `merkle` array. */
export interface MerkleMap {
    uniqueId: number
    localId: number
    count: number
    alg?: string
    initHash?: Uint8Array
    /** Stored Merkle row (usually a single root hash). */
    hashes: Uint8Array[]
}

/** A per-fragment `uuid`/"merkle" box. */
export interface FragmentMerkle {
    uniqueId: number
    localId: number
    location: number
    /** Proof hashes from leaf up to the stored row. */
    hashes: Uint8Array[]
}

function toBytes (v: unknown): Uint8Array {
    if (v instanceof Uint8Array) return v
    if (Array.isArray(v)) return new Uint8Array(v as number[])
    return new Uint8Array(0)
}

/** Parses the `merkle` array of a decoded `c2pa.hash.bmff.*` assertion. */
export function parseMerkleMaps (assertionData: Record<string, unknown>): MerkleMap[] {
    const merkle = assertionData.merkle
    if (!Array.isArray(merkle)) return []
    return merkle.map(m => {
        const o = m as Record<string, unknown>
        return {
            uniqueId: Number(o.uniqueId),
            localId: Number(o.localId),
            count: Number(o.count),
            alg: typeof o.alg === 'string' ? o.alg : undefined,
            initHash: o.initHash !== undefined ? toBytes(o.initHash) : undefined,
            hashes: Array.isArray(o.hashes) ? o.hashes.map(toBytes) : []
        }
    })
}

/** Decodes a merkle `uuid` box's CBOR map to a FragmentMerkle, or null if malformed. */
function decodeFragmentMerkle (cbor: Uint8Array): FragmentMerkle | null {
    // The merkle `uuid` box is a framed container that may carry trailing padding
    // after the CBOR map (observed: 1–3 zero bytes), so read the first item and
    // ignore the remainder rather than requiring an exact-length decode.
    let o: Record<string, unknown>
    try {
        o = decodeFirst(cbor).value as Record<string, unknown>
    } catch {
        return null
    }
    if (!o || typeof o !== 'object') return null
    return {
        uniqueId: Number(o.uniqueId),
        localId: Number(o.localId),
        location: Number(o.location),
        hashes: Array.isArray(o.hashes) ? o.hashes.map(toBytes) : []
    }
}

/** Parses a media fragment's first `uuid`/"merkle" box, or null if absent. */
export function parseFragmentMerkle (fragment: Uint8Array): FragmentMerkle | null {
    const box = readC2paBox(fragment)
    if (!box || box.purpose !== 'merkle') return null
    return decodeFragmentMerkle(box.cbor)
}

/**
 * Parses EVERY `uuid`/"merkle" box in a media fragment (one per track in a muxed
 * fragment). Returns the list, or `null` if any C2PA merkle box is present but its
 * CBOR is malformed — so a corrupt second-track box cannot be silently dropped
 * (BMFF-1). An empty list means no merkle box was found at all.
 */
export function parseAllFragmentMerkle (fragment: Uint8Array): FragmentMerkle[] | null {
    const out: FragmentMerkle[] = []
    for (const box of findAllC2paBoxes(fragment)) {
        const content = parseC2paBox(fragment, box)
        if (!content || content.purpose !== 'merkle') continue
        const fm = decodeFragmentMerkle(content.cbor)
        if (!fm) return null // a merkle box that fails to decode is a hard failure
        out.push(fm)
    }
    return out
}

/** c2pa-rs `C2PAMerkleTree::to_layout`: the node count of each tree layer, leaves first. */
export function toLayout (numLeaves: number): number[] {
    const layers = [numLeaves]
    let current = numLeaves
    while (current > 1) {
        let parent = 0
        for (let i = 0; i < current; i += 2) parent++
        layers.push(parent)
        current = parent
    }
    return layers
}

async function sha (alg: string, a: Uint8Array, b: Uint8Array): Promise<Uint8Array> {
    const buf = new Uint8Array(a.length + b.length)
    buf.set(a, 0)
    buf.set(b, a.length)
    return new Uint8Array(await crypto.subtle.digest(alg, buf as unknown as ArrayBuffer))
}

function equal (a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
    return true
}

/**
 * Climbs a leaf hash to the stored Merkle row using the fragment's proof and
 * checks it against `map.hashes`. Mirrors c2pa-rs `check_merkle_tree`.
 */
export async function checkMerkleTree (digest: string, map: MerkleMap, leaf: Uint8Array, location: number, proof: Uint8Array[]): Promise<boolean> {
    if (location >= map.count) return false
    let index = location
    let hash = leaf
    let proofIndex = 0
    for (const layer of toLayout(map.count)) {
        if (layer === map.hashes.length) break
        const isRight = index % 2 === 1
        if (isRight) {
            if (index - 1 < layer) {
                const p = proof[proofIndex++]
                if (!p) return false
                hash = await sha(digest, p, hash)
            }
        } else if (index + 1 < layer) {
            const p = proof[proofIndex++]
            if (!p) return false
            hash = await sha(digest, hash, p)
        }
        index = Math.floor(index / 2)
    }
    const stored = map.hashes[index]
    return stored !== undefined && equal(stored, hash)
}

/** Verifies the init-segment hash for a Merkle map. */
export async function verifyInitHash (initBytes: Uint8Array, map: MerkleMap, exclusions: BmffExclusion[], assertionAlg?: string, claimAlg?: string): Promise<boolean> {
    if (!map.initHash) return true // nothing to check
    const digest = resolveDigest(map.alg, assertionAlg, claimAlg)
    const computed = await computeBmffHash(initBytes, { exclusions, alg: digest, bmffV2: true })
    return equal(computed, map.initHash)
}

/**
 * Verifies a media fragment against the manifest's Merkle maps. Reads EVERY
 * `uuid`/merkle box in the fragment (one per muxed track) and requires each to
 * match a map by uniqueId/localId and climb its proof to that map's stored row —
 * the fragment's leaf is its BMFF content hash (which excludes all C2PA `uuid`
 * boxes). Any fragment merkle box with no matching map, or a failing tree, fails
 * the whole fragment (BMFF-1). A fragment with no merkle box at all → false.
 * Mirrors c2pa-rs `verify_stream_segment`.
 */
export async function verifyFragment (fragment: Uint8Array, maps: MerkleMap[], exclusions: BmffExclusion[], assertionAlg?: string, claimAlg?: string): Promise<boolean> {
    const fms = parseAllFragmentMerkle(fragment)
    if (!fms || fms.length === 0) return false
    for (const fm of fms) {
        const map = maps.find(m => m.uniqueId === fm.uniqueId && m.localId === fm.localId)
        if (!map) return false // §18.6.6: a fragment merkle box with no matching map
        const digest = resolveDigest(map.alg, assertionAlg, claimAlg)
        const leaf = await computeBmffHash(fragment, { exclusions, alg: digest, bmffV2: true })
        if (!await checkMerkleTree(digest, map, leaf, fm.location, fm.hashes)) return false
    }
    return true
}
